import type { FC } from "react";
import type React from "react";
import type { JSX } from "react";
import type { IDashboardNavItemConfig } from "app/components/sidebar3/secondary-side-bar/components/DashboardsNavBar/DashboardNavBarTypes";
import {
    DeleteConfirmationTooltip,
    DeleteUnShareConfirmationTooltip,
    PopupClickContainer,
    DashboardItem,
    EDashboardItemState,
} from "@similarweb/ui-components";
import styled from "styled-components";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

interface IDashboardNavItemProps {
    itemConfig: IDashboardNavItemConfig;
    onClick: (id: string, title: string, itemIndex: number, groupTitle: string) => void;
    getMenuItems: () => React.ReactElement[];
    onMenuItemClick: (menuItem: any, navItem: IDashboardNavItemConfig) => void;
    onCancelDelete: () => void;
    onConfirmDelete: (navItem: IDashboardNavItemConfig) => void;
    onConfirmEdit: (newItemText: string) => void;
    onCancelEdit: () => void;
    itemIndex: number;
    groupTitle: string;
}

const ItemDeleteContainer = styled.div`
    border-radius: 6px;
    background-color: white;
`;

export const DashboardNavItem: FC<IDashboardNavItemProps> = (props) => {
    const {
        itemConfig,
        onClick,
        getMenuItems,
        onMenuItemClick,
        onCancelDelete,
        onConfirmDelete,
        onConfirmEdit,
        onCancelEdit,
        itemIndex,
        groupTitle,
    } = props;
    const t = useTranslation();
    const getItemType = () => {
        if (itemConfig.isEditable) {
            return EDashboardItemState.Rename;
        }
        if (itemConfig.deletePopUpIsOpen) {
            return EDashboardItemState.Delete;
        }
        return EDashboardItemState.Default;
    };
    const itemElement = (
        <DashboardItem
            dashboardState={getItemType()}
            itemText={itemConfig.title}
            key={itemConfig.id}
            dashboardId={itemConfig.id}
            isSelected={!itemConfig.deletePopUpIsOpen ? itemConfig.isActive : null}
            isCopiedToUser={itemConfig.isSharedByMe}
            isLoading={itemConfig.isLoading}
            onClick={() => onClick(itemConfig.id, itemConfig.title, itemIndex, groupTitle)}
            getMenuItems={getMenuItems}
            onMenuItemClick={(menuItem) => onMenuItemClick(menuItem, itemConfig)}
            onClickConfirm={(id, newTitle) => onConfirmEdit(newTitle)}
            onCancel={onCancelEdit}
        />
    );

    const renderDeleteConfirmationTooltip = (): JSX.Element => {
        // The user should be notified that his item will be unshared
        // from other users
        return itemConfig.isSharedByMe ? (
            <DeleteUnShareConfirmationTooltip
                onConfirm={() => onConfirmDelete(itemConfig)}
                onCancel={() => onCancelDelete()}
                translations={{
                    title: t("sidenav.delete.unshare.tooltip.title"),
                    subtitle: t("sidenav.delete.unshare.tooltip.subtitle"),
                    buttonText: t("sidenav.delete.unshare.tooltip.buttonText"),
                    cancelText: t("sidenav.delete.unshare.tooltip.cancelText"),
                }}
            />
        ) : (
            <DeleteConfirmationTooltip
                onConfirm={() => onConfirmDelete(itemConfig)}
                onCancel={() => onCancelDelete()}
                translations={{
                    title: t("sidenav.delete.tooltip.title"),
                    subtitle: t("sidenav.delete.tooltip.subtitle"),
                    buttonText: t("sidenav.delete.tooltip.buttonText"),
                    cancelText: t("sidenav.delete.tooltip.cancelText"),
                }}
            />
        );
    };

    const renderItemWithDeleteTooltip = (): JSX.Element => {
        return (
            <PopupClickContainer
                content={renderDeleteConfirmationTooltip}
                config={{
                    placement: "right",
                    cssClass: "deleteConfirmation--tooltip",
                    width: 415,
                    defaultOpen: true,
                    onToggle: (isOpen: boolean, isOutsideClick: boolean) => isOutsideClick && onCancelDelete(),
                }}
            >
                <ItemDeleteContainer>{itemElement}</ItemDeleteContainer>
            </PopupClickContainer>
        );
    };

    if (itemConfig.deletePopUpIsOpen) {
        return renderItemWithDeleteTooltip();
    }
    return itemElement;
};
